export interface RichRun { text: string; bold?: boolean; italic?: boolean; underline?: boolean }
export type RichBlock =
  | { type: "paragraph"; runs: RichRun[] }
  | { type: "unordered_list" | "ordered_list"; items: RichRun[][] };

const keys = (value: Record<string, unknown>, allowed: string[]) => Object.keys(value).every(key => allowed.includes(key));
function runs(value: unknown): RichRun[] {
  if (!Array.isArray(value) || value.length > 200) throw new Error("Invalid line details");
  return value.map(entry => {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) throw new Error("Invalid line details");
    const run = entry as Record<string, unknown>;
    if (!keys(run, ["text", "bold", "italic", "underline"]) || typeof run.text !== "string" || run.text.includes("\0") || run.text.length > 5000) throw new Error("Invalid line details");
    for (const key of ["bold", "italic", "underline"]) if (run[key] !== undefined && typeof run[key] !== "boolean") throw new Error("Invalid line details");
    return { text: run.text, ...(run.bold ? { bold: true } : {}), ...(run.italic ? { italic: true } : {}), ...(run.underline ? { underline: true } : {}) };
  });
}

/** Strict JSON document model; HTML is never accepted at the server boundary. */
export function parseRichDetails(value: unknown): RichBlock[] {
  if (!Array.isArray(value) || value.length > 100) throw new Error("Invalid line details");
  const blocks: RichBlock[] = value.map(entry => {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) throw new Error("Invalid line details");
    const block = entry as Record<string, unknown>;
    if (block.type === "paragraph" && keys(block, ["type", "runs"])) return { type: "paragraph", runs: runs(block.runs) };
    if ((block.type === "unordered_list" || block.type === "ordered_list") && keys(block, ["type", "items"]) && Array.isArray(block.items) && block.items.length <= 100) return { type: block.type, items: block.items.map(runs) };
    throw new Error("Invalid line details");
  });
  if (plainRichDetails(blocks).length > 5000) throw new Error("Line details are too long");
  return blocks;
}

export function plainRichDetails(blocks: RichBlock[]): string {
  return blocks.map(block => block.type === "paragraph" ? block.runs.map(run => run.text).join("") : block.items.map((item,index) => `${block.type === "unordered_list" ? "•" : `${index + 1}.`} ${item.map(run => run.text).join("")}`).join("\n")).join("\n");
}

export function legacyLineContent(description: string): { title: string; details: RichBlock[] } {
  const parts = description.replace(/\r\n?/g, "\n").split("\n");
  const first = parts.findIndex(part => part.trim());
  if (first < 0) return { title: "", details: [] };
  const title = parts[first]!.trim();
  const details: RichBlock[] = [];
  let paragraph: string[] = [];
  let bullets: RichRun[][] = [];
  const flushParagraph = () => { const text = paragraph.join("\n").trim(); if (text) details.push({ type: "paragraph", runs: [{ text }] }); paragraph = []; };
  const flushBullets = () => { if (bullets.length) details.push({ type: "unordered_list", items: bullets }); bullets = []; };
  for (const part of parts.slice(first + 1)) {
    const bullet = part.match(/^\s*•\s+(.+)$/);
    if (bullet) { flushParagraph(); bullets.push([{ text: bullet[1]!.trim() }]); }
    else { flushBullets(); paragraph.push(part); }
  }
  flushBullets(); flushParagraph();
  return { title, details };
}

export function lineSearchText(title: string, details: RichBlock[]): string {
  return [title.trim(), plainRichDetails(details)].filter(Boolean).join("\n");
}
